import React, { ReactNode } from 'react';
import { SWRConfig } from 'swr';
import { persistCacheProvider } from 'src/swr';
// Cognito AuthStateProvider + AuthBootstrap deleted 2026-04-20 as part
// of the full Cognito → Clerk cutover. Auth state now flows through
// Clerk's <ClerkProvider> in src/index.jsx; consumers read it via
// `useAuth` / `useUser` from `@clerk/clerk-react` (or the shim hook
// at src/hooks/auth/useAuth.ts which preserves the legacy
// JWTContextType return shape).
import { ContractStateProvider } from "./ContractStateContext";
import { AudioProvider } from 'src/hooks/useAudio';
import { CardComparisonProvider } from './CardComparisonContext';
import { OrgProvider } from './OrgContext';
import { PostHogProvider } from 'posthog-js/react';
import { ThemeProvider } from './ThemeContext';
import { FlowProvider } from '@/features/guided-flow';

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * Clean provider architecture with proper separation of concerns
 * - State contexts only hold state and basic setters
 * - Business logic is handled in hooks
 * - No circular dependencies between contexts
 */
export const AppProviders: React.FC<AppProvidersProps> = ({ children }) => {
  const posthogEnabled = import.meta.env.VITE_POSTHOG_ENABLED === 'true' && !!import.meta.env.VITE_POSTHOG_KEY;
  const apiHost = import.meta.env.PROD
    ? '/Hogger' // Use proxy in production to avoid ad blockers
    : import.meta.env.VITE_POSTHOG_HOST; // Direct connection in dev

  const content = (
    <SWRConfig value={{
      provider: persistCacheProvider,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }}>
      <ThemeProvider>
        <AudioProvider>
          <ContractStateProvider>
            <OrgProvider>
              <CardComparisonProvider>
                <FlowProvider>
                  {children}
                </FlowProvider>
              </CardComparisonProvider>
            </OrgProvider>
          </ContractStateProvider>
        </AudioProvider>
      </ThemeProvider>
    </SWRConfig>
  );

  if (!posthogEnabled) {
    return content;
  }

  return (
    <PostHogProvider
      apiKey={import.meta.env.VITE_POSTHOG_KEY || ''}
      options={{
        api_host: apiHost,
        defaults: '2025-05-24',
        feature_flag_request_timeout_ms: 3000,
      }}
    >
      {content}
    </PostHogProvider>
  );
};
